import { cn } from '@/utils';
import React, {
	memo,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

import { Popover } from 'antd';
// import { Colors } from '@/themes'; // optional: restore if you have it
// import { cn } from '@/utils'; // optional

export type TStackedProgressItem = {
	key?: React.Key;
	label: React.ReactNode;
	labelText?: React.ReactNode;
	percent: number; // 0..100 (raw value; sum may not equal 100)
	color?: string;
	labelColor?: string;
	content?: {
		title?: string;
		count?: number;
		unit?: string;
	}[];
};

export interface IStackedProgressProps {
	title?: React.ReactNode;
	items: TStackedProgressItem[];
	className?: string;
	barHeight?: number; // px
	trackColor?: string;
	minLabelGap?: number; // px
	labelOffset?: number; // px distance from bar
	showLabels?: boolean;
}

type TLabelPos = {
	left: number; // px
	row: 0 | 1; // 0 = below, 1 = above
	width: number; // px
} | null;

const clamp = (value: number, min: number, max: number) =>
	Math.max(min, Math.min(max, value));

const defaultColors = {
	track: 'transparent',
	seg: '#6782F9',
	font: '#222222',
};

const StackedProgress: React.FC<IStackedProgressProps> = ({
	title,
	items,
	className,
	barHeight = 14,
	trackColor = defaultColors.track,
	minLabelGap = 12,
	labelOffset = 12,
	showLabels = true,
}) => {
	const containerRef = useRef<HTMLDivElement | null>(null);

	// hidden measurement container ref (contains spans for each label)
	const measureRef = useRef<HTMLDivElement | null>(null);

	// label positions, aligned by items index
	const [labelPositions, setLabelPositions] = useState<TLabelPos[]>(() =>
		items.map(() => null)
	);

	// mounted flag to trigger width animation from 0 -> value
	const [mounted, setMounted] = useState(false);

	// normalize items (compute ratio based on total of percent)
	const normalized = React.useMemo(() => {
		const total = items.reduce((s, it) => s + (it.percent || 0), 0);
		if (total <= 0) return items.map((it) => ({ ...it, ratio: 0 }));
		return items.map((it) => ({ ...it, ratio: (it.percent || 0) / total }));
	}, [items]);

	// compute label positions function
	const computeLabelPositions = useCallback(() => {
		const cont = containerRef.current;
		const measure = measureRef.current;
		if (!cont || !measure || !showLabels) {
			setLabelPositions(items.map(() => null));
			return;
		}

		const containerWidth = cont.clientWidth;
		if (!containerWidth) {
			setLabelPositions(items.map(() => null));
			return;
		}

		// Measure label widths from the hidden measurement spans (same order as items)
		const spans = Array.from(
			measure.querySelectorAll<HTMLSpanElement>('.__sp-measure')
		);
		const widths = items.map((_, i) => {
			const s = spans[i];
			if (!s) return 0;
			const rect = s.getBoundingClientRect();
			return Math.ceil(rect.width);
		});

		// Build segment metrics (px coordinates)
		const segments: {
			index: number;
			leftPx: number;
			segPx: number;
			centerX: number;
			width: number;
			percent: number;
			row?: 0 | 1;
		}[] = [];

		let runningLeft = 0;
		const totalRatio =
			normalized.reduce((s, it) => s + (it.ratio || 0), 0) || 1;
		for (let i = 0; i < normalized.length; i++) {
			const r = normalized[i].ratio || 0;
			const segPx = (r / totalRatio) * containerWidth; // px of this segment
			const leftPx = runningLeft;
			const centerX = leftPx + segPx / 2;
			segments.push({
				index: i,
				leftPx,
				segPx,
				centerX,
				width: widths[i] || 0,
				percent: items[i].percent || 0,
			});
			runningLeft += segPx;
		}

		// Active metrics (only items with percent>0 and width > 0)
		const metrics = segments
			.filter((s) => (s.percent || 0) > 0)
			.map((s) => {
				const desiredLeft = s.centerX - s.width / 2;
				const minLeft = s.leftPx;
				const maxLeft = s.leftPx + s.segPx - s.width; // if label fits inside seg
				return {
					...s,
					desiredLeft,
					minLeft,
					maxLeft,
					adjustedLeft: desiredLeft,
				};
			});

		// Avoid overlap: left-to-right pass (enforce min gap)
		metrics.sort((a, b) => a.adjustedLeft - b.adjustedLeft);
		for (let i = 1; i < metrics.length; i++) {
			const prev = metrics[i - 1];
			const cur = metrics[i];
			const needed = prev.adjustedLeft + prev.width + minLabelGap;
			if (cur.adjustedLeft < needed) cur.adjustedLeft = needed;
		}

		// Right-to-left clamp to container edges and keep min gap
		for (let i = metrics.length - 1; i >= 0; i--) {
			const cur = metrics[i];
			cur.adjustedLeft = clamp(
				cur.adjustedLeft,
				0,
				Math.max(0, containerWidth - cur.width)
			);
			if (i < metrics.length - 1) {
				const next = metrics[i + 1];
				const maxLeft = next.adjustedLeft - cur.width - minLabelGap;
				if (cur.adjustedLeft > maxLeft) cur.adjustedLeft = maxLeft;
			}
		}

		if (metrics.length > 0) {
			const first = metrics[0];
			if (first.adjustedLeft < 0) first.adjustedLeft = 0;

			const last = metrics[metrics.length - 1];
			const maxLeft = containerWidth - last.width;
			if (last.adjustedLeft > maxLeft) {
				last.adjustedLeft = maxLeft;
			}
		}

		// Finally, ensure within segment bounds again (helpful after shifting)
		for (const m of metrics) {
			// Căn giữa bar
			let desired = m.centerX - m.width / 2;

			if (m.segPx < m.width) {
				// Bar nhỏ hơn label ⇒ cố gắng giữa bar
				desired = m.centerX - m.width / 2;
			}

			// Nếu tràn trái ⇒ ghim về trái
			if (desired < 0) {
				desired = 0;
			}

			// Nếu tràn phải ⇒ ghim về phải
			if (desired + m.width > containerWidth) {
				desired = containerWidth - m.width;
			}

			m.adjustedLeft = desired;
		}

		// Assign rows greedily to minimize vertical collisions (two rows)
		const rowLastRight = [-Infinity, -Infinity]; // last right coordinate in each row
		metrics.sort((a, b) => a.adjustedLeft - b.adjustedLeft);
		for (const m of metrics) {
			const left = m.adjustedLeft;
			const right = left + m.width;
			// choose row 0 if it doesn't overlap; else row1 if it doesn't overlap; else smaller overlap
			if (rowLastRight[0] <= left) {
				m['row'] = 0;
				rowLastRight[0] = right + minLabelGap;
			} else if (rowLastRight[1] <= left) {
				m['row'] = 1;
				rowLastRight[1] = right + minLabelGap;
			} else {
				// both overlap; pick the row with smaller right
				const pick = rowLastRight[0] <= rowLastRight[1] ? 0 : 1;
				m['row'] = pick;
				rowLastRight[pick] = Math.max(rowLastRight[pick], right + minLabelGap);
			}
		}

		// Map back to original index positions array
		const out: TLabelPos[] = items.map(() => null);
		for (const m of metrics) {
			let adjustedLeft = m.adjustedLeft;

			if (m.index === 0) {
				// Label đầu tiên không được tràn trái
				adjustedLeft = Math.max(0, adjustedLeft);
			}
			if (m.index === items.length - 1) {
				// Label cuối cùng không được tràn phải
				adjustedLeft = Math.min(adjustedLeft, containerWidth - m.width);
			}

			out[m.index] = {
				left: Math.round(adjustedLeft),
				row: (m as any).row as 0 | 1,
				width: m.width,
			};
		}

		setLabelPositions(out);
	}, [items, normalized, minLabelGap, showLabels]);

	// measure after render - useLayoutEffect to avoid flicker
	useLayoutEffect(() => {
		computeLabelPositions();
		// set mounted to true for animated widths after first paint
		// use requestAnimationFrame to ensure layout done
		requestAnimationFrame(() => setMounted(true));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [computeLabelPositions]);

	// single ResizeObserver on container to recompute on resize
	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const ro = new ResizeObserver(() => {
			computeLabelPositions();
		});
		ro.observe(el);

		const onW = () => computeLabelPositions();
		window.addEventListener('resize', onW);

		return () => {
			ro.disconnect();
			window.removeEventListener('resize', onW);
		};
	}, [computeLabelPositions]);

	// prepare segment styles (left% & width% via normalized ratios)
	const segPercents = React.useMemo(() => {
		const total = normalized.reduce((s, it) => s + (it.ratio || 0), 0) || 1;
		let cum = 0;
		return normalized.map((it) => {
			const w = (it.ratio / total) * 100;
			const left = cum;
			cum += w;
			return { left, width: w, color: it.color || defaultColors.seg };
		});
	}, [normalized]);

	const getContent = useCallback((item: TStackedProgressItem) => {
		const { content, labelText } = item || {};
		if (!content?.length || content?.length === 0) return null;

		return (
			<div className="w-[200px] p-3" onClick={(e) => e.stopPropagation()}>
				{labelText && (
					<div className="mb-2">
						<p className="text-[11px] text-font2 font-semibold">{labelText}</p>
					</div>
				)}
				<ul className="max-h-48 flex flex-col gap-y-1.5 overflow-y-auto overflow-x-hidden">
					{content?.map((item, index) => {
						return (
							<li key={index} className="text-xs text-font5 flex">
								<span className="truncate" title={item?.title}>
									- {item?.title}
								</span>
								{typeof item.count === 'number' && (
									<span className="whitespace-nowrap">
										: {item?.count}
										{item.unit}
									</span>
								)}
							</li>
						);
					})}
				</ul>
			</div>
		);
	}, []);

	return (
		<div className={cn('py-[26px] px-5 border border-[#E5E5E5]', className)}>
			{/* title */}
			{title &&
				(typeof title === 'string' ? (
					<div className="text-sm font-bold text-font2">{title}</div>
				) : (
					title
				))}
			<div ref={containerRef} className="relative">
				{/* Hidden measurement spans */}
				{showLabels && (
					<div
						ref={measureRef}
						aria-hidden
						style={{
							position: 'absolute',
							left: 0,
							top: 0,
							visibility: 'hidden',
							pointerEvents: 'none',
							whiteSpace: 'nowrap',
						}}
					>
						{items.map((it, i) => (
							<span
								key={i}
								className="__sp-measure"
								style={{
									display: 'inline-block',
									fontSize: 13,
									fontWeight: 500,
								}}
							>
								{it.label}
							</span>
						))}
					</div>
				)}

				{/* Above labels (row === 1) */}
				{showLabels && (
					<div
						style={{
							position: 'relative',
							height: 14,
							marginBottom: labelOffset,
						}}
					>
						{items.map((it, i) => {
							const pos = labelPositions[i];
							if (!pos || pos.row !== 1) return null;
							return (
								<div
									key={`top-${it.key ?? i}`}
									style={{
										height: 14,
										position: 'absolute',
										left: pos.left,
										top: 0,
										whiteSpace: 'nowrap',
										fontSize: 14,
										fontWeight: 500,
										color: it.labelColor || defaultColors.font,
									}}
								>
									<Popover
										prefixCls="calendar-progress-popover"
										overlayClassName="!rounded-[5px] border border-gray4"
										overlayInnerStyle={{
											borderRadius: '5px',
											padding: 0,
										}}
										placement="top"
										content={getContent(it)}
										trigger={'hover'}
										arrow={false}
										destroyTooltipOnHide
									>
										{it.label}
									</Popover>
								</div>
							);
						})}
					</div>
				)}

				{/* Track */}
				<div
					style={{
						width: '100%',
						height: barHeight,
						background: trackColor,
						borderRadius: barHeight / 2,
						overflow: 'hidden',
						position: 'relative',
					}}
				>
					{segPercents.map((s, i) => {
						const show = (items[i].percent || 0) > 0 && s.width > 0.0001;
						return (
							<div
								key={i}
								style={{
									position: 'absolute',
									left: `${s.left}%`,
									top: 0,
									bottom: 0,
									width: mounted && show ? `${s.width}%` : '0%',
									background: s.color,
								}}
							/>
						);
					})}
				</div>

				{/* Below labels (row === 0) */}
				{showLabels && (
					<div
						style={{ position: 'relative', height: 14, marginTop: labelOffset }}
					>
						{items.map((it, i) => {
							const pos = labelPositions[i];
							if (!pos || pos.row !== 0) return null;
							return (
								<div
									key={`bot-${it.key ?? i}`}
									style={{
										height: 14,
										position: 'absolute',
										left: pos.left,
										top: 0,
										whiteSpace: 'nowrap',
										fontSize: 13,
										fontWeight: 500,
										color: it.labelColor || defaultColors.font,
									}}
								>
									<Popover
										prefixCls="calendar-progress-popover"
										overlayClassName="!rounded-[5px] border border-gray4"
										overlayInnerStyle={{
											borderRadius: '5px',
											padding: 0,
										}}
										placement="bottom"
										content={getContent(it)}
										trigger={'hover'}
										arrow={false}
										destroyTooltipOnHide
									>
										{it.label}
									</Popover>
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
};

export default memo(StackedProgress);
